import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';

interface RuntimeEnv {
  apiUrl?: string;
  gateWay?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthBootstrapService {
  private readonly tokenRequestChannelName = 'tokenRequest';
  private readonly tokenRefreshChannelName = 'tokenRefresh';
  private channelsInitialized = false;

  constructor(private http: HttpClient) {}

  async ensureAuthenticated(): Promise<boolean> {
    const env = this.ensureRuntimeEnv();
    const bypassAuth = sessionStorage.getItem('bypassAuth') === 'true';
    const isLocal = this.isLocalHost();

    if (bypassAuth) {
      return true;
    }

    this.initializeCrossTabChannels();

    if (this.isLocalHost() && sessionStorage.getItem('Authorization') === null) {
      sessionStorage.setItem('Authorization', '');
    }

    let authorization = sessionStorage.getItem('Authorization');
    if (!authorization || authorization === 'null') {
      const tokenFromTab = await this.tryGetTokenFromAnotherTab();
      if (tokenFromTab) {
        sessionStorage.setItem('Authorization', tokenFromTab);
      }
    }

    authorization = sessionStorage.getItem('Authorization');
    if (!authorization || authorization === 'null') {
      const authCode = this.getQueryParam('code');
      if (authCode) {
        const identityToken = await this.fetchIdentityToken(env, authCode);
        if (identityToken) {
          const bearerToken = `Bearer ${identityToken}`;
          sessionStorage.setItem('Authorization', bearerToken);
          this.broadcastTokenRefresh(bearerToken);
          this.clearOAuthCodeFromUrl();
        }
      }
    }

    authorization = sessionStorage.getItem('Authorization');
    if (authorization === 'null') {
      sessionStorage.removeItem('Authorization');
      authorization = null;
    }

    // Legacy flow proceeds to /getToken whenever Authorization is not null (empty string allowed).
    if (authorization === null) {
      if (isLocal) {
        return true;
      }
      this.redirectToOAuth(env);
      return false;
    }

    const tokenState = await this.ensureGatewayToken(authorization);
    if (tokenState === 'ok') {
      return true;
    }

    if (tokenState === 'forbidden') {
      if (isLocal) {
        return true;
      }
      this.redirectToDenied();
      return false;
    }

    if (tokenState === 'unauthorized') {
      if (isLocal) {
        return true;
      }
      sessionStorage.removeItem('Authorization');
      this.redirectToOAuth(env);
      return false;
    }

    if (isLocal) {
      return true;
    }

    sessionStorage.removeItem('Authorization');
    this.redirectToOAuth(env);
    return false;
  }

  private ensureRuntimeEnv(): RuntimeEnv {
    const globalWindow = window as Window & { __env?: RuntimeEnv };
    const current = globalWindow.__env || {};
    const defaultGateway = window.location.origin;
    const merged: RuntimeEnv = {
      apiUrl: current.apiUrl || environment.base_url,
      gateWay: current.gateWay || defaultGateway,
    };

    globalWindow.__env = merged;
    return merged;
  }

  private async tryGetTokenFromAnotherTab(): Promise<string | null> {
    if (typeof BroadcastChannel === 'undefined') {
      return null;
    }

    return new Promise<string | null>((resolve) => {
      const channel = new BroadcastChannel(this.tokenRequestChannelName);
      let settled = false;

      const finalize = (token: string | null): void => {
        if (settled) {
          return;
        }
        settled = true;
        channel.close();
        resolve(token);
      };

      channel.onmessage = (event: MessageEvent<string>) => {
        if (event.data && event.data !== 'request-token') {
          finalize(event.data);
        }
      };

      channel.postMessage('request-token');
      window.setTimeout(() => finalize(null), 500);
    });
  }

  private initializeCrossTabChannels(): void {
    if (this.channelsInitialized || typeof BroadcastChannel === 'undefined') {
      return;
    }

    const tokenRequestChannel = new BroadcastChannel(this.tokenRequestChannelName);
    const tokenRefreshChannel = new BroadcastChannel(this.tokenRefreshChannelName);

    tokenRequestChannel.onmessage = (event: MessageEvent<string>) => {
      if (event.data === 'request-token') {
        const currentToken = sessionStorage.getItem('Authorization');
        if (currentToken !== null) {
          tokenRequestChannel.postMessage(currentToken);
        }
        return;
      }

      const existingToken = sessionStorage.getItem('Authorization');
      if (existingToken && existingToken !== 'null') {
        return;
      }

      sessionStorage.setItem('Authorization', event.data);
      void this.ensureGatewayToken(event.data);
    };

    tokenRefreshChannel.onmessage = (event: MessageEvent<string>) => {
      const existingToken = sessionStorage.getItem('Authorization');
      if (!existingToken || existingToken === 'null') {
        return;
      }

      sessionStorage.setItem('Authorization', event.data);
    };

    this.channelsInitialized = true;
  }

  private broadcastTokenRefresh(token: string): void {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    const tokenRefreshChannel = new BroadcastChannel(this.tokenRefreshChannelName);
    tokenRefreshChannel.postMessage(token);
    tokenRefreshChannel.close();
  }

  private async fetchIdentityToken(env: RuntimeEnv, code: string): Promise<string | null> {
    if (!env.gateWay) {
      return null;
    }

    const url = `${env.gateWay}/gtw/getIdentityToken?authorization_code=${encodeURIComponent(code)}`;

    try {
      const response = await firstValueFrom(
        this.http.get<string | { id_token?: string }>(url, {
          withCredentials: true,
          responseType: 'text' as 'json',
        })
      );

      if (typeof response === 'string') {
        const parsed = JSON.parse(response) as { id_token?: string };
        return parsed.id_token || null;
      }

      return response?.id_token || null;
    } catch {
      return null;
    }
  }

  private async ensureGatewayToken(
    authorization: string
  ): Promise<'ok' | 'unauthorized' | 'forbidden' | 'failed'> {
    const clientKey = this.getQueryParam('client_key');
    const suffix = clientKey ? `?client_key=${encodeURIComponent(clientKey)}` : '';
    const tokenUrl = `${environment.base_url}/getToken${suffix}`;

    try {
      const headers = new HttpHeaders({ Authorization: authorization });
      await firstValueFrom(
        this.http.get(tokenUrl, {
          headers,
          withCredentials: true,
        })
      );
      return 'ok';
    } catch (error: unknown) {
      const status = (error as HttpErrorResponse)?.status;
      if (status === 401) {
        return 'unauthorized';
      }
      if (status === 403) {
        return 'forbidden';
      }
      return 'failed';
    }
  }

  private redirectToOAuth(env: RuntimeEnv): void {
    if (!env.gateWay) {
      return;
    }

    window.location.replace(`${env.gateWay}/gtw/oauth2/authorization/gtw-services`);
  }

  private redirectToDenied(): void {
    window.location.href = 'app/templates/denied.html';
  }

  private clearOAuthCodeFromUrl(): void {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('code')) {
      return;
    }

    url.searchParams.delete('code');
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  }

  private getQueryParam(paramName: string): string | null {
    const url = new URL(window.location.href);
    return url.searchParams.get(paramName);
  }

  private isLocalHost(): boolean {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }
}